'use client'

import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import Image from 'next/image';
import Link from "next/link";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signIn } from "next-auth/react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { signInSchema } from "@/lib/zod";
import type { z } from 'zod';
import { toast } from '@/components/ui/use-toast'
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, Lock, Mail } from 'lucide-react';
import LogoDeD from '@/assets/logoded.png';
import ImagePrincipal from '@/assets/ded_theme.png';

type SignInFormData = z.infer<typeof signInSchema>;

export function AuthForm() {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<SignInFormData>({
        resolver: zodResolver(signInSchema),
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const router = useRouter();

    const onSubmit = async (data: SignInFormData) => {
        setIsLoading(true);
        try {
            const result = await signIn("credentials", {
                email: data.email,
                password: data.password,
                redirect: false,
            });

            if (result?.error) {
                let errorMessage = 'Ocorreu um erro. Tente novamente!';
                switch (result.error) {
                    case 'CredentialsSignin':
                        errorMessage = 'Email ou senha inválidos.';
                        break;
                    case 'AccessDenied':
                        errorMessage = 'Acesso negado.';
                        break;
                }

                toast({
                    title: 'Erro de Autenticação',
                    description: errorMessage,
                    variant: 'destructive'
                });
            } else if (result?.ok) {
                toast({
                    title: 'Sucesso',
                    description: 'Login realizado com sucesso!',
                    variant: 'default'
                });

                router.push('/system');
                window.location.reload();
            }
        } catch (error) {
            console.error('Login error:', error);
            toast({
                title: 'Erro',
                description: 'Ocorreu um erro ao tentar fazer login. Tente novamente.',
                variant: 'destructive'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="grid min-h-dvh w-full lg:grid-cols-2">
            {/* Brand / image panel */}
            <aside className="relative isolate flex min-h-[34vh] flex-col justify-end overflow-hidden lg:min-h-dvh">
                <Image
                    src={ImagePrincipal}
                    alt="Operação D&D Ambiental"
                    fill
                    priority
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="object-cover object-center"
                />
                <div
                    className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--sidebar-bg))]/95 via-[hsl(var(--sidebar-bg))]/45 to-[hsl(var(--sidebar-bg))]/15 lg:bg-gradient-to-br lg:from-[hsl(var(--sidebar-bg))]/80 lg:via-[hsl(var(--sidebar-bg))]/35 lg:to-transparent"
                    aria-hidden
                />

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="relative z-10 space-y-3 p-6 sm:p-8 lg:p-12 lg:pb-14"
                >
                    <p className="font-quicksand text-xs font-semibold uppercase tracking-[0.22em] text-[hsl(var(--sidebar-fg-muted))]">
                        D&amp;D Ambiental
                    </p>
                    <h1 className="max-w-md font-quicksand text-2xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl">
                        Gestão inteligente para operações sustentáveis
                    </h1>
                    <p className="hidden max-w-sm text-sm leading-relaxed text-white/75 lg:block">
                        Controle frota, equipes e processos em um só lugar — com a clareza que o dia a dia exige.
                    </p>
                </motion.div>
            </aside>

            {/* Credentials panel — D&D primary green with readable form container */}
            <section className="relative flex flex-1 items-center justify-center bg-primary px-5 py-8 sm:px-8 lg:px-12">
                <div
                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--secondary)/0.35),transparent_55%),radial-gradient(ellipse_at_bottom_left,hsl(var(--sidebar-bg-strong)/0.45),transparent_50%)]"
                    aria-hidden
                />

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="relative z-10 w-full max-w-[420px] rounded-2xl border border-border bg-background p-6 shadow-[0_24px_60px_-20px_hsl(var(--sidebar-bg)/0.55)] sm:p-8"
                >
                    <div className="mb-7 flex flex-col items-start gap-5">
                        <Image
                            src={LogoDeD}
                            alt="Logotipo D&D"
                            width={160}
                            height={40}
                            priority
                            className="h-9 w-auto"
                        />
                        <div>
                            <h2 className="font-quicksand text-2xl font-bold tracking-tight text-foreground sm:text-[1.75rem]">
                                Bem-vindo de volta
                            </h2>
                            <p className="mt-1.5 text-sm text-muted-foreground">
                                Entre com seu email e senha para acessar o sistema.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-sm font-medium text-foreground">
                                Email
                            </Label>
                            <div className="relative">
                                <Mail
                                    className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                                    aria-hidden
                                />
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    disabled={isLoading}
                                    {...register('email', { shouldUnregister: true })}
                                    placeholder="email@ded.com.br"
                                    className={`h-12 rounded-xl border-input bg-background pl-10 shadow-sm focus-visible:ring-2 focus-visible:ring-ring/30 ${
                                        errors.email ? "border-destructive focus-visible:ring-destructive/30" : "hover:border-primary/40"
                                    }`}
                                />
                            </div>
                            {errors.email && (
                                <p className="text-sm text-destructive" role="alert">{errors.email.message}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between gap-3">
                                <Label htmlFor="password" className="text-sm font-medium text-foreground">
                                    Senha
                                </Label>
                                <Link
                                    href="#"
                                    className="text-xs font-medium text-primary transition-colors hover:text-secondary"
                                    prefetch={false}
                                >
                                    Esqueci a senha
                                </Link>
                            </div>
                            <div className="relative">
                                <Lock
                                    className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                                    aria-hidden
                                />
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    disabled={isLoading}
                                    {...register('password', { shouldUnregister: true })}
                                    placeholder="••••••••"
                                    className={`h-12 rounded-xl border-input bg-background pl-10 pr-11 shadow-sm focus-visible:ring-2 focus-visible:ring-ring/30 ${
                                        errors.password ? "border-destructive focus-visible:ring-destructive/30" : "hover:border-primary/40"
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="text-sm text-destructive" role="alert">{errors.password.message}</p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            size="lg"
                            className="mt-1 h-12 w-full rounded-xl text-base font-semibold transition-transform active:scale-[0.98]"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Entrando…
                                </span>
                            ) : (
                                'Entrar'
                            )}
                        </Button>
                    </form>

                    <p className="mt-7 text-center text-xs text-muted-foreground">
                        © {new Date().getFullYear()} D&amp;D Ambiental · Acesso restrito
                    </p>
                </motion.div>
            </section>
        </div>
    );
}

export default AuthForm;
